import { ApiService } from '@services/ApiService'
import { useEffect, useRef, useState } from 'react'

import { VOICE_ERROR_MESSAGES } from '../errorMessages'

import {
  isVoiceUnavailableCode,
  voiceErrorCode,
  voiceErrorText,
} from './voiceErrors'
import { VOICE_MAX_BYTES, VoiceRecorder } from './voiceRecorder'

/** starting: waiting for the microphone (the browser may be asking the user for it). */
export type VoiceInputState = 'idle' | 'starting' | 'recording' | 'transcribing'

interface VoiceInputOptions {
  /** The recording stops by itself at this length and is transcribed. */
  maxSeconds: number
  /** The text heard; never empty. It is the caller's to put in the composer — never sent. */
  onTranscript: (_text: string) => void
  /** The server said voice input or the assistant is not available (any more). */
  onUnavailable?: () => void
}

const TICK_MS = 250

/**
 * Voice input of the composer: record → stop → the server transcribes → the text is handed to
 * `onTranscript`. One at a time, from the click on the microphone to the transcript. Cancel (or
 * the composer going away) throws the recording away, releases the microphone and abandons the
 * request. Neither the audio nor the text is kept here.
 */
export const useVoiceInput = ({
  maxSeconds,
  onTranscript,
  onUnavailable,
}: VoiceInputOptions) => {
  const [state, setState] = useState<VoiceInputState>('idle')
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const recorderRef = useRef<VoiceRecorder | null>(null)
  const requestRef = useRef<AbortController | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // From the click on the microphone to the transcript: a second click does nothing meanwhile.
  const busyRef = useRef(false)
  const mountedRef = useRef(true)
  // The callbacks of the latest render: the transcript arrives long after the click.
  const optionsRef = useRef({ maxSeconds, onTranscript, onUnavailable })
  optionsRef.current = { maxSeconds, onTranscript, onUnavailable }

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  /** Back to idle; the error stays as it is. */
  const reset = () => {
    clearTimer()
    recorderRef.current = null
    requestRef.current = null
    busyRef.current = false
    if (mountedRef.current) {
      setState('idle')
      setElapsedSeconds(0)
    }
  }

  const showError = (message: string | null) => {
    if (mountedRef.current) {
      setError(message)
    }
  }

  const transcribe = async (recorder: VoiceRecorder) => {
    clearTimer()
    if (mountedRef.current) {
      setState('transcribing')
    }
    const recording = await recorder.stop()
    // Cancelled while the recorder handed over the audio.
    if (recorderRef.current !== recorder) {
      return
    }
    recorderRef.current = null
    if (!recording) {
      reset()
      showError(VOICE_ERROR_MESSAGES.NO_SPEECH)
      return
    }
    if (recording.audio.size > VOICE_MAX_BYTES) {
      reset()
      showError(VOICE_ERROR_MESSAGES.AI_VOICE_INVALID_AUDIO)
      return
    }
    const controller = new AbortController()
    requestRef.current = controller
    try {
      const res = await ApiService.Assistant.Transcribe(
        recording.audio,
        recording.fileName,
        controller.signal
      )
      if (controller.signal.aborted || !mountedRef.current) {
        return
      }
      const text =
        typeof res?.data?.text === 'string' ? res.data.text.trim() : ''
      reset()
      if (!text) {
        showError(VOICE_ERROR_MESSAGES.NO_SPEECH)
        return
      }
      optionsRef.current.onTranscript(text)
    } catch (err) {
      // Abandoned (cancel, the composer went away): not a failure.
      if (controller.signal.aborted || !mountedRef.current) {
        return
      }
      reset()
      showError(voiceErrorText(err))
      if (isVoiceUnavailableCode(voiceErrorCode(err))) {
        optionsRef.current.onUnavailable?.()
      }
    }
  }

  /** Asks for the microphone (only now) and starts recording. */
  const start = async () => {
    if (busyRef.current) {
      return
    }
    busyRef.current = true
    setError(null)
    // The permission prompt can wait for the user (or hide in the address bar): show it, with Cancel.
    setState('starting')
    const recorder = new VoiceRecorder(() => {
      if (recorderRef.current === recorder) {
        reset()
        showError(VOICE_ERROR_MESSAGES.RECORDING_FAILED)
      }
    })
    recorderRef.current = recorder
    try {
      const started = await recorder.start()
      if (!started || recorderRef.current !== recorder) {
        return
      }
    } catch (err) {
      if (recorderRef.current !== recorder) {
        return
      }
      reset()
      showError(voiceErrorText(err))
      return
    }
    if (!mountedRef.current) {
      recorder.cancel()
      return
    }
    const startedAt = Date.now()
    setElapsedSeconds(0)
    setState('recording')
    timerRef.current = setInterval(() => {
      const seconds = Math.floor((Date.now() - startedAt) / 1000)
      setElapsedSeconds(seconds)
      if (seconds >= optionsRef.current.maxSeconds) {
        transcribe(recorder)
      }
    }, TICK_MS)
  }

  /** Ends the recording and transcribes it. */
  const stop = () => {
    const recorder = recorderRef.current
    if (!recorder?.isRecording) {
      return
    }
    transcribe(recorder)
  }

  /** Throws the recording away, or abandons its transcription. */
  const cancel = () => {
    requestRef.current?.abort()
    recorderRef.current?.cancel()
    reset()
    setError(null)
  }

  const clearError = () => setError(null)

  // Leaving (the Guide or the History opened, the drawer gone): the microphone is released and
  // the request abandoned; nothing arrives in a composer that is no longer there.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      clearTimer()
      requestRef.current?.abort()
      recorderRef.current?.cancel()
      recorderRef.current = null
      requestRef.current = null
      busyRef.current = false
    }
  }, [])

  return {
    state,
    elapsedSeconds,
    error,
    start,
    stop,
    cancel,
    clearError,
  }
}

export type VoiceInput = ReturnType<typeof useVoiceInput>
